import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useRef, useState } from 'react';
import { type Instance as TippyInstance } from 'tippy.js/index';
import imageRecolorOffIcon from '../assets/icons/imageRecolorOff.svg';
import imageRecolorOnIcon from '../assets/icons/imageRecolorOn.svg';
import imageRecolorModeMenuIcon from '../assets/icons/imageRecolorModeMenu.svg';
import WhiteboardController from '../controllers/WhiteboardController';
import { isShapeImage } from '../objects/guards';
import type Shape from '../objects/Shape';
import type ShapeImage from '../shapes/ShapeImage';
import { type colorFilterMode } from '../shapes/ShapeImage';
import ToolButton from '../toolbars/ToolButton';
import { colorPaletteForObject } from '../utils/DefaultsManager';
import { svgToIcon } from '../utils/svg';
import { ColorPalette, OpacitySlider } from './ColorSwatch';
import Tool, { type ToolButtonComponent } from './Tool';
import Tippy from '@tippyjs/react';
import ToolbarVDivider from '../toolbars/ToolbarVDivider';
import styles from '../styles.module.css';
import classNames from 'classnames';

// Defines the layout of the menu. The second value in the tuple
// is the displayed name of the filter.
const filterModeMenuOptions: Array<[colorFilterMode, string]>[] = [
  [['color', 'Normal']],
  [
    ['overlay', 'Overlay'],
    ['multiply', 'Multiply'],
    ['darken', 'Darken'],
    ['lighten', 'Lighten'],
    ['hue', 'Hue'],
    ['saturation', 'Saturation'],
  ],
];

export const Popup: React.FC<{
  currentColor: string;
  setCurrentColor: (color: string) => void;
  shapeImage: Shape<ShapeImage>;
  hidePopup: () => void;
  showOpacitySilder?: boolean;
  setOpacity: (opacity: number) => void;
  paletteTippyRef: React.MutableRefObject<TippyInstance | undefined>;
}> = ApplicationView(
  ({
    currentColor,
    setCurrentColor,
    shapeImage,
    hidePopup,
    setOpacity,
    paletteTippyRef,
  }) => {
    const handleOpacityChange = (opacity: number) => {
      shapeImage.set('colorFilterOpacity', opacity);
      setOpacity(opacity);
    };
    const tippyRef = useRef<TippyInstance>();
    const [shouldRenderPopup, setShouldRenderPopup] = useState(false);
    const controller = useController(WhiteboardController);

    const ticks = '[2, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]';
    const opacity = shapeImage.colorFilterOpacity;

    const [colorFilterMode, setColorFilterMode] = useState(
      shapeImage.colorFilterMode
    );

    const palette = colorPaletteForObject(shapeImage);
    // Apply a default colour if the user changes the filter mode without
    // first having selected a colour. Otherwise the mode menu would do nothing,
    // which might be confusing to the user. We considered disabing the menu
    // if no colour was selected but decided to do this instead.
    const defaultColor = palette[13];

    const colorModePopup = (
      <div className={styles.toolImageColorModePopup}>
        {filterModeMenuOptions.map((section, index) => (
          <React.Fragment key={index}>
            {section.map(([filterMode, name]) => {
              return (
                <aha-menu-item
                  selected={filterMode === colorFilterMode ? true : undefined}
                  key={filterMode}
                  onMouseEnter={() => {
                    shapeImage.set('colorFilterPreviewMode', filterMode);
                    if (currentColor === 'transparent') {
                      shapeImage.set('colorFilterPreviewColor', defaultColor);
                    }
                    controller.canvas.requestRenderAll();
                  }}
                  onClick={() => {
                    setColorFilterMode(filterMode);
                    if (currentColor === 'transparent') {
                      setCurrentColor(defaultColor);
                    }
                    shapeImage.set('colorFilterPreviewMode', null);
                    shapeImage.set('colorFilterMode', filterMode);
                    // Without requestRenderAll the mode change won't render until you mouse over
                    // the image. I don't understand why because opacity and colour changes
                    // render immediately.
                    controller.canvas.requestRenderAll();
                    tippyRef.current?.hide();
                  }}
                  data-testId={`colorFilterMode-${name}`}
                >
                  {name}
                </aha-menu-item>
              );
            })}
            {index < filterModeMenuOptions.length - 1 && <ToolbarVDivider />}
          </React.Fragment>
        ))}
      </div>
    );

    const currentFilterModeMenuEntry = filterModeMenuOptions
      .flat()
      .find(([mode]) => mode === colorFilterMode);
    if (!currentFilterModeMenuEntry) {
      throw new Error(
        `Did not find a color filter menu entry for ${colorFilterMode}`
      );
    }

    return (
      <ColorPalette
        palette={palette}
        currentColor={currentColor}
        setCurrentColor={setCurrentColor}
        hidePopup={hidePopup}
        onColorHover={color => {
          shapeImage.set('colorFilterPreviewColor', color);
          controller.canvas.requestRenderAll();
        }}
      >
        <div style={{ width: '100%' }}>
          <OpacitySlider
            ticks={ticks}
            opacity={opacity}
            handleOpacityChange={handleOpacityChange}
          />
          <ToolbarVDivider />
          <Tippy
            arrow={false}
            placement='right-start'
            hideOnClick={true}
            offset={() => {
              // This code positions the menu to the right of the palette and
              // aligns it with the top of the palette
              const palettePopper = paletteTippyRef.current?.popper;
              if (!palettePopper) return [0, 0];

              const paletteRect = palettePopper.getBoundingClientRect();
              const referenceRect =
                tippyRef.current?.reference.getBoundingClientRect();
              if (!referenceRect) return [0, 0];

              return [
                paletteRect.top - referenceRect.top,
                3 + (paletteRect.right - referenceRect.right),
              ];
            }}
            content={shouldRenderPopup ? colorModePopup : null}
            onShow={instance => {
              if (tippyRef) tippyRef.current = instance;
              setShouldRenderPopup(true);
            }}
            onHide={() => setShouldRenderPopup(false)}
            theme='light'
            trigger='click'
            interactive
            duration={75}
            animation='scale'
          >
            <div
              className={styles.toolImageColorModeMenu}
              data-testid='colorFilterModeMenu'
            >
              <div className={styles.toolImageColorModeMenuText}>
                {svgToIcon(imageRecolorModeMenuIcon)}
                <div>{currentFilterModeMenuEntry[1]}</div>
              </div>
              <i
                className={classNames(
                  styles.toolImageColorModeMenuArrow,
                  'fa-regular',
                  'fa-chevron-right'
                )}
              />
            </div>
          </Tippy>
          <ToolbarVDivider />
        </div>
      </ColorPalette>
    );
  }
);

const imageColorButton = (): ToolButtonComponent => {
  return ApplicationView(({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();
    if (!isShapeImage(selection)) {
      throw new Error('Image colour can only be applied to images');
    }

    const colorFilterColor = selection.colorFilterColor;
    const [currentColor, setCurrentColor] = useState(colorFilterColor);
    const [opacity, setOpacity] = useState<number>(
      selection.colorFilterOpacity
    );

    return (
      <ToolButton
        popup={
          <Popup
            currentColor={currentColor}
            setCurrentColor={(color: string) => {
              setCurrentColor(color);
              selection.set('colorFilterColor', color);
              selection.set('colorFilterPreviewColor', null);
            }}
            shapeImage={selection}
            hidePopup={() => tippyRef.current?.hide()}
            showOpacitySilder={true}
            setOpacity={setOpacity}
            paletteTippyRef={tippyRef}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
        name={'Recolor image'}
      >
        {currentColor !== 'transparent'
          ? svgToIcon(
              imageRecolorOnIcon
                .replace('fillColorPlaceholder', currentColor)
                .replace('opacityPlaceholder', opacity.toString())
            )
          : svgToIcon(imageRecolorOffIcon)}
      </ToolButton>
    );
  });
};

class ToolRecolorImage extends Tool {
  static toolName = 'Recolor Image';

  static buttonComponent() {
    return imageColorButton();
  }
}

export default ToolRecolorImage;
